import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useParams, useNavigate } from 'react-router-dom';
import { getQuizResultDetails } from '@/services/quizGameService';
import { QuizResultDetail, QuizUserAnswer } from '@/models/quiz-game/types';
import { format, formatDuration, intervalToDuration } from 'date-fns';
import { vi } from 'date-fns/locale';
import { supabase } from '@/lib/supabase';
import { FaArrowLeft, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaRegStar, FaStar } from 'react-icons/fa';
import Confetti from 'react-confetti';
import useWindowSize from '@/hooks/useWindowSize';

// UI Components từ shadcn/ui
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Trophy, 
  Clock, 
  ArrowLeft, 
  Award,
  CheckCircle,
  XCircle,
  FileText,
  BarChart2,
  Clipboard,
  Check,
  X,
  AlertTriangle,
  Share2,
  Printer,
  Download,
  Loader2
} from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

const QuizResultDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { resultId } = useParams<{ resultId: string }>();
  const { width, height } = useWindowSize();
  
  const [result, setResult] = useState<QuizResultDetail | null>(null);
  const [quizGame, setQuizGame] = useState<any>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [userAnswers, setUserAnswers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (resultId) {
      loadResultDetails();
    }
  }, [resultId, user]);
  
  const loadResultDetails = async () => {
    setLoading(true);
    
    try {
      if (!resultId) return;
      
      const resultData = await getQuizResultDetails(resultId);
      console.log('Quiz result details loaded:', resultData);
      
      setResult(resultData);
      setQuizGame(resultData.game);
      
      // Fetch quiz questions
      const { data: questionsData, error: questionsError } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('game_id', resultData.game_id)
        .order('order');
      
      if (questionsError) {
        console.error('Error fetching quiz questions:', questionsError);
      } else if (questionsData) {
        console.log('Quiz questions:', questionsData);
        setQuestions(questionsData);
        
        // Parse user answers if available
        if (resultData.user_answers) {
          try {
            const parsedAnswers = typeof resultData.user_answers === 'string' 
              ? JSON.parse(resultData.user_answers) 
              : resultData.user_answers;
            console.log('Parsed user answers:', parsedAnswers);
            setUserAnswers(parsedAnswers);
          } catch (e) {
            console.error('Error parsing user answers:', e);
          }
        }
        
        // Show confetti for good scores
        const scorePercentage = (resultData.score / (questionsData.length * 10)) * 100;
        if (scorePercentage >= 70) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 5000);
        }
      }
    } catch (error) {
      console.error('Error loading quiz result details:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: vi });
    } catch (e) {
      return dateString;
    }
  };
  
  const formatTime = (seconds: number) => {
    try {
      // Chuyển đổi seconds thành object duration
      const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
      
      // Format duration
      return formatDuration(duration, {
        format: ['minutes', 'seconds'],
        locale: vi,
        zero: true,
        delimiter: ' và '
      });
    } catch (e) {
      // Fallback đơn giản
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins} phút ${secs} giây`;
    }
  };
  
  const getScoreColorClass = (score: number) => {
    if (score >= 90) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (score >= 70) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (score >= 50) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };
  
  const getScoreTextClass = (score: number) => {
    if (score >= 90) return 'text-emerald-700';
    if (score >= 70) return 'text-blue-700';
    if (score >= 50) return 'text-amber-700';
    return 'text-red-700';
  };
  
  const getScoreVariant = (score: number): "default" | "secondary" | "destructive" | "outline" | "success" | null => {
    if (score >= 80) return "success";
    if (score >= 60) return "secondary";
    if (score >= 40) return "default";
    return "destructive";
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  const getAnswerForQuestion = (questionId: string) => {
    return userAnswers.find(ans => ans.questionId === questionId);
  };
  
  const renderQuestionResult = (question: any, index: number) => {
    const userAnswer = getAnswerForQuestion(question.id);
    const isCorrect = userAnswer?.isCorrect || false;
    
    // Parse correct answer safely
    let correctAnswer = null;
    try {
      if (typeof question.correct_answer === 'string' && question.correct_answer.startsWith('{')) {
        correctAnswer = JSON.parse(question.correct_answer);
      } else {
        correctAnswer = question.correct_answer;
      }
    } catch (e) {
      correctAnswer = question.correct_answer;
    }
    
    // Parse options safely
    let options = [];
    try {
      if (typeof question.options === 'string') {
        options = JSON.parse(question.options);
      } else if (Array.isArray(question.options)) {
        options = question.options;
      }
    } catch (e) {
      options = [];
    }
    
    return (
      <Card key={question.id} className="mb-4">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Badge className="mr-2">{`#${index + 1}`}</Badge>
              <CardTitle className="text-lg font-medium">
                {question.question || question.questionText}
              </CardTitle>
            </div>
            <Badge variant={isCorrect ? "success" : "destructive"}>
              {isCorrect ? (
                <div className="flex items-center">
                  <Check className="w-4 h-4 mr-1" />
                  {t('quiz.correct')}
                </div>
              ) : (
                <div className="flex items-center">
                  <X className="w-4 h-4 mr-1" />
                  {t('quiz.incorrect')}
                </div>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Options */}
            <div className="space-y-2">
              {options.map((option: any, i: number) => {
                const optionText = typeof option === 'string' ? option : option.text || '';
                const isUserChoice = userAnswer && (
                  Array.isArray(userAnswer.userAnswer) 
                    ? userAnswer.userAnswer.includes(optionText)
                    : userAnswer.userAnswer === optionText
                );
                
                const isCorrectOption = Array.isArray(correctAnswer)
                  ? correctAnswer.includes(optionText)
                  : correctAnswer === optionText;
                
                return (
                  <div 
                    key={i}
                    className={cn(
                      "p-3 rounded-md border flex items-center",
                      isUserChoice && isCorrectOption && "bg-green-50 border-green-300",
                      isUserChoice && !isCorrectOption && "bg-red-50 border-red-300",
                      !isUserChoice && isCorrectOption && "bg-blue-50 border-blue-300",
                      !isUserChoice && !isCorrectOption && "bg-gray-50 border-gray-200"
                    )}
                  >
                    <div className="mr-2">
                      {isUserChoice && isCorrectOption && <CheckCircle className="w-5 h-5 text-green-600" />}
                      {isUserChoice && !isCorrectOption && <XCircle className="w-5 h-5 text-red-600" />}
                      {!isUserChoice && isCorrectOption && <CheckCircle className="w-5 h-5 text-blue-600" />}
                      {!isUserChoice && !isCorrectOption && <span className="w-5 h-5 inline-block text-center">{String.fromCharCode(65 + i)}</span>}
                    </div>
                    <div>
                      {optionText}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Explanation if available */}
            {question.explanation && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <div className="font-medium text-amber-800 mb-1">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  {t('quiz.explanation')}:
                </div>
                <div className="text-amber-700">{question.explanation}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };
  
  if (loading) {
    return (
      <div className="container mx-auto py-8 flex flex-col items-center justify-center min-h-[50vh]">
        <Spinner size="xl" />
        <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }
  
  if (!result) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-destructive mb-4">{t('quiz.resultNotFound')}</h2>
          <Button onClick={() => navigate('/quiz/history')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.backToHistory')}
          </Button>
        </div>
      </div>
    );
  }
  
  // Calculate scores and percentages
  const totalQuestions = result.totalQuestions || questions.length || 0;
  const correctAnswersCount = result.correctAnswers || 0;
  const scorePercentage = totalQuestions > 0 ? (result.score / (totalQuestions * 10)) * 100 : 0;
  const correctPercentage = totalQuestions > 0 ? (correctAnswersCount / totalQuestions) * 100 : 0;
  
  return (
    <>
      {showConfetti && <Confetti width={width} height={height} recycle={false} />}
      
      <div className="container mx-auto py-8 print:py-2">
        {/* Nút quay lại và in kết quả */}
        <div className="flex justify-between items-center mb-6 print:hidden">
          <Button 
            variant="outline"
            onClick={() => navigate('/quiz/history')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.backToHistory')}
          </Button>
          
          <Button onClick={handlePrint}>
            <Printer className="mr-2 h-4 w-4" />
            {t('common.print')}
          </Button>
        </div>
        
        {/* Tiêu đề */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">{t('quiz.resultTitle')}</h1>
          <p className="text-muted-foreground">
            {quizGame?.title || t('quiz.unknownQuiz')}
          </p>
        </div>
        
        {/* Tổng quan kết quả */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t('quiz.summary')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Cột 1: Thông tin */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-col">
                    <span className="text-sm text-muted-foreground">{t('quiz.startTime')}</span>
                    <span className="font-medium">{formatDate(result.startedAt)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-muted-foreground">{t('quiz.endTime')}</span>
                    <span className="font-medium">{formatDate(result.completedAt)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-muted-foreground">{t('quiz.timeSpent')}</span>
                    <span className="font-medium">{formatTime(result.timeSpent)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm text-muted-foreground">{t('quiz.questionCount')}</span>
                    <span className="font-medium">{totalQuestions}</span>
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('quiz.correctAnswers')}</span>
                    <Badge variant="outline" className="font-mono">
                      {correctAnswersCount}/{totalQuestions}
                    </Badge>
                  </div>
                  <Progress value={correctPercentage} className="h-2" />
                  
                  <div className="flex items-center justify-between mt-4">
                    <span className="font-medium">{t('quiz.totalScore')}</span>
                    <Badge variant={getScoreVariant(scorePercentage)} className="font-mono text-md">
                      {result.score}/{totalQuestions * 10}
                    </Badge>
                  </div>
                  <Progress value={scorePercentage} className="h-2" />
                </div>
              </div>
              
              {/* Cột 2: Đánh giá */}
              <div className={cn(
                "rounded-lg border p-6 flex flex-col items-center justify-center text-center",
                getScoreColorClass(scorePercentage)
              )}>
                <div className="mb-4">
                  {scorePercentage >= 80 ? (
                    <Trophy className="h-12 w-12 text-yellow-500" />
                  ) : scorePercentage >= 60 ? (
                    <Award className="h-12 w-12 text-blue-500" />
                  ) : (
                    <FileText className="h-12 w-12 text-gray-500" />
                  )}
                </div>
                
                <h3 className="text-xl font-bold mb-2 mt-2">
                  {scorePercentage >= 80 ? t('quiz.excellent') : 
                   scorePercentage >= 60 ? t('quiz.good') : 
                   scorePercentage >= 40 ? t('quiz.fair') : 
                   t('quiz.needsImprovement')}
                </h3>
                
                <div className="text-4xl font-bold mb-4 mt-2">
                  {scorePercentage.toFixed(0)}%
                </div>
                
                <p className="text-sm">
                  {scorePercentage >= 80 
                    ? t('quiz.excellentMessage') 
                    : scorePercentage >= 60 
                    ? t('quiz.goodMessage')
                    : scorePercentage >= 40 
                    ? t('quiz.fairMessage')
                    : t('quiz.improvementMessage')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Chi tiết câu hỏi */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-4">{t('quiz.questionDetails')}</h2>
          <Tabs defaultValue="all">
            <TabsList className="mb-4">
              <TabsTrigger value="all">{t('quiz.allQuestions')}</TabsTrigger>
              <TabsTrigger value="correct">{t('quiz.correctAnswers')}</TabsTrigger>
              <TabsTrigger value="incorrect">{t('quiz.incorrectAnswers')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-2">
              {questions.map((question, index) => renderQuestionResult(question, index))}
            </TabsContent>
            
            <TabsContent value="correct" className="mt-2">
              {questions
                .filter(q => {
                  const userAnswer = getAnswerForQuestion(q.id);
                  return userAnswer?.isCorrect;
                })
                .map((question, index) => renderQuestionResult(question, index))}
            </TabsContent>
            
            <TabsContent value="incorrect" className="mt-2">
              {questions
                .filter(q => {
                  const userAnswer = getAnswerForQuestion(q.id);
                  return !userAnswer?.isCorrect;
                })
                .map((question, index) => renderQuestionResult(question, index))}
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Buttons */}
        <div className="flex justify-between mt-8 print:hidden">
          <Button 
            variant="outline"
            onClick={() => navigate('/quiz/history')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.backToHistory')}
          </Button>
          
          <Button 
            onClick={() => navigate(`/quiz/play/${result.gameId}`)}
          >
            <Loader2 className="mr-2 h-4 w-4" />
            {t('quiz.tryAgain')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default QuizResultDetailPage; 